import axios from 'axios';

import {
  PacientePostFormData,
  PacientePutFormData,
  ResponseObj,
  PacientesPaginatedData,
  DocumentoAlterarFormData,
} from '../../../back/src/types';
import store from '../store';
import { Paciente } from '../../../back/src/entity/Paciente';
import { Consulta } from '../../../back/src/entity/Consulta';
import { Historico } from '../../../back/src/entity/Historico';
import { PlanoDeSaude } from '../../../back/src/entity/PlanoDeSaude';
import { ResultadoDeExame } from '../../../back/src/entity/ResultadoDeExame';
import { Ultrassom } from '../../../back/src/entity/Ultrassom';
import { Vacinacao } from '../../../back/src/entity/Vacinacao';
import { Gestacao } from '../../../back/src/entity/Gestacao';
import { InformacaoParto } from '../../../back/src/entity/InformacaoParto';
import { HistoricoResposta } from '../../../back/src/entity/HistoricoResposta';
import { Internacao } from '../../../back/src/entity/Internacao';
import { Documento } from '../../../back/src/entity/Documento';
import { NotaProntuario } from '../../../back/src/entity/NotaProntuario';

const paciente = async (pacienteId: number): Promise<ResponseObj<Paciente>> => {
  const res = await axios.get<ResponseObj<Paciente>>(`pacientes/${pacienteId}`);
  return res.data;
};

const pacientes = async (queryObj: Object = {}): Promise<ResponseObj<PacientesPaginatedData>> => {
  const res = await axios.get<ResponseObj<PacientesPaginatedData>>('pacientes', {
    params: queryObj,
  });
  return res.data;
};

const pacientesGestantes = async (queryObj: Object = {}): Promise<ResponseObj<Paciente[]>> => {
  const res = await axios.get<ResponseObj<Paciente[]>>('pacientes/gestantes', { params: queryObj });
  return res.data;
};

const carteiraPreNatal = async (pacienteId: number, gestacaoId: number): Promise<any> => {
  const res = await axios.get<any>(`pacientes/${pacienteId}/carteira/${gestacaoId}`, {
    responseType: 'arraybuffer',
  });
  return res.data;
};

const prontuarioCompleto = async (
  pacienteId: number,
  queryObj: { preNatalOnly: boolean },
): Promise<any> => {
  const res = await axios.get<any>(`pacientes/${pacienteId}/prontuario_completo`, {
    params: queryObj,
    responseType: 'arraybuffer',
  });
  return res.data;
};

const criaPacienteExterno = async (
  formData: PacientePostFormData,
): Promise<ResponseObj<Paciente>> => {
  const res = await axios.post<ResponseObj<Paciente>>('pacientes/cadastro', formData);
  return res.data;
};

const criaPaciente = async (formData: PacientePostFormData): Promise<ResponseObj<Paciente>> => {
  const res = await axios.post<ResponseObj<Paciente>>('pacientes', formData);
  return res.data;
};

const alteraPaciente = async (formData: PacientePutFormData): Promise<ResponseObj<Paciente>> => {
  const res = await axios.put<ResponseObj<Paciente>>('pacientes', formData);
  return res.data;
};

const validaCpf = async (cpf: string): Promise<ResponseObj<null>> => {
  const res = await axios.get<ResponseObj<null>>(`pacientes/valida_cpf/${cpf}`);
  return res.data;
};

const validaEmail = async (email: string): Promise<ResponseObj<null>> => {
  const res = await axios.get<ResponseObj<null>>(`pacientes/valida_email/${email}`);
  return res.data;
};

const gestacoes = async (
  pacienteId: number,
  queryObj: Object = {},
): Promise<ResponseObj<Gestacao[]>> => {
  const res = await axios.get<ResponseObj<Gestacao[]>>(`pacientes/${pacienteId}/gestacoes`, {
    params: queryObj,
  });
  return res.data;
};

const consultas = async (pacienteId: number): Promise<ResponseObj<Consulta[]>> => {
  const res = await axios.get<ResponseObj<Consulta[]>>(`pacientes/${pacienteId}/consultas`);
  return res.data;
};

const historicos = async (pacienteId: number): Promise<ResponseObj<Historico[]>> => {
  const res = await axios.get<ResponseObj<Historico[]>>(`pacientes/${pacienteId}/historicos`);
  return res.data;
};

const historicoResposta = async (
  pacienteId: number,
  historicoPerguntaId: number,
): Promise<ResponseObj<HistoricoResposta>> => {
  const res = await axios.get<ResponseObj<HistoricoResposta>>(
    `pacientes/${pacienteId}/historico_resposta/${historicoPerguntaId}`,
  );
  return res.data;
};

const informacoesPartos = async (pacienteId: number): Promise<ResponseObj<InformacaoParto[]>> => {
  const res = await axios.get<ResponseObj<InformacaoParto[]>>(
    `pacientes/${pacienteId}/informacoes_partos`,
  );
  return res.data;
};

const internacoes = async (pacienteId: number): Promise<ResponseObj<Internacao[]>> => {
  const res = await axios.get<ResponseObj<Internacao[]>>(`pacientes/${pacienteId}/internacoes`);
  return res.data;
};

const planosDeSaude = async (pacienteId: number): Promise<ResponseObj<PlanoDeSaude[]>> => {
  const res = await axios.get<ResponseObj<PlanoDeSaude[]>>(
    `pacientes/${pacienteId}/planos_de_saude`,
  );
  return res.data;
};

const resultadosDeExame = async (pacienteId: number): Promise<ResponseObj<ResultadoDeExame[]>> => {
  const res = await axios.get<ResponseObj<ResultadoDeExame[]>>(
    `pacientes/${pacienteId}/resultados_de_exames`,
  );
  return res.data;
};

const resultadosDeExameRemovidos = async (
  pacienteId: number,
): Promise<ResponseObj<ResultadoDeExame[]>> => {
  const res = await axios.get<ResponseObj<ResultadoDeExame[]>>(
    `pacientes/${pacienteId}/resultados_de_exames/removidos`,
  );
  return res.data;
};

const tipoSanguineo = async (pacienteId: number): Promise<ResponseObj<string | null>> => {
  const res = await axios.get<ResponseObj<string | null>>(`pacientes/${pacienteId}/tipo_sanguineo`);
  return res.data;
};

const ultrassons = async (pacienteId: number): Promise<ResponseObj<Ultrassom[]>> => {
  const res = await axios.get<ResponseObj<Ultrassom[]>>(`pacientes/${pacienteId}/ultrassons`);
  return res.data;
};

const uploadFoto = async (formData): Promise<ResponseObj<Paciente>> => {
  const res = await axios.put<ResponseObj<Paciente>>('pacientes/foto', formData, {
    headers: {
      Authorization: `Bearer ${store.getters.token}`,
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data;
};

const criaDocumento = async (pacienteId: number, formData): Promise<ResponseObj<Documento>> => {
  const res = await axios.post<ResponseObj<Documento>>(
    `pacientes/${pacienteId}/documentos`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${store.getters.token}`,
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return res.data;
};

const alteraDocumento = async (
  pacienteId: number,
  documentoId: number,
  formData: DocumentoAlterarFormData,
): Promise<ResponseObj<Documento>> => {
  const res = await axios.put<ResponseObj<Documento>>(
    `pacientes/${pacienteId}/documentos/${documentoId}`,
    formData,
  );
  return res.data;
};

const documentos = async (pacienteId: number): Promise<ResponseObj<Documento[]>> => {
  const res = await axios.get<ResponseObj<Documento[]>>(`pacientes/${pacienteId}/documentos`);
  return res.data;
};

const excluirDocumento = async (
  pacienteId: number,
  documentoId: number,
): Promise<ResponseObj<void>> => {
  const res = await axios.delete<ResponseObj<void>>(
    `pacientes/${pacienteId}/documentos/${documentoId}`,
  );
  return res.data;
};

const vacinacoes = async (pacienteId: number): Promise<ResponseObj<Vacinacao[]>> => {
  const res = await axios.get<ResponseObj<Vacinacao[]>>(`pacientes/${pacienteId}/vacinacoes`);
  return res.data;
};

const notasProntuario = async (pacienteId: number): Promise<ResponseObj<NotaProntuario[]>> => {
  const res = await axios.get<ResponseObj<NotaProntuario[]>>(
    `pacientes/${pacienteId}/notas_prontuarios`,
  );
  return res.data;
};

const vacinacoesRemovidas = async (pacienteId: number): Promise<ResponseObj<Vacinacao[]>> => {
  const res = await axios.get<ResponseObj<Vacinacao[]>>(
    `pacientes/${pacienteId}/vacinacoes/removidas`,
  );
  return res.data;
};

export default {
  alteraDocumento,
  alteraPaciente,
  carteiraPreNatal,
  consultas,
  criaDocumento,
  criaPaciente,
  criaPacienteExterno,
  documentos,
  excluirDocumento,
  gestacoes,
  historicoResposta,
  historicos,
  informacoesPartos,
  internacoes,
  notasProntuario,
  paciente,
  pacientes,
  pacientesGestantes,
  planosDeSaude,
  prontuarioCompleto,
  resultadosDeExame,
  resultadosDeExameRemovidos,
  tipoSanguineo,
  ultrassons,
  uploadFoto,
  vacinacoes,
  vacinacoesRemovidas,
  validaCpf,
  validaEmail,
};
